class MainMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.details.querySelector('summary');
    this.megaMenu = this.details.querySelector('.mega-menu');
    this.megaMenuInner = this.details.querySelector('.mega-menu__inner');
    this.closeButton = this.querySelector('[data-close]');

    this.setupDetailsObserver();

    this.init();
  }

  debounce(fn, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        fn(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupDetailsObserver() {
    // Create mutation observer
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'open') {
          if (!this.details.hasAttribute('open')) {
            // Reset background height
            document.documentElement.style.setProperty('--mega-menu-background-height', '0px');
          }
        }
      });
    });

    // Start observing the details element for attribute changes
    observer.observe(this.details, {
      attributes: true,
      attributeFilter: ['open'] // only watch the 'open' attribute
    });
  }

  init() {
    // Remove existing click events by cloning the summary
    const newSummary = this.summary.cloneNode(true);
    this.summary.parentNode.replaceChild(newSummary, this.summary);
    this.summary = newSummary;

    // Add hover handlers for primary menu items
    if (this.summary.classList.contains('header__menu-item--primary')) {
      this.megaMenu.addEventListener('mouseleave', () => {
        this.closeMenu();
      });
    }

    // Add click handler
    this.summary.addEventListener('mouseenter', this.onSummaryClick.bind(this));
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
    });

    // Prevent default details behavior
    this.details.addEventListener('toggle', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    // Close menu when clicking outside
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target) && this.details.hasAttribute('open')) {
        this.closeMenu();
      }
    });

    this.closeButton.addEventListener('mouseenter', () => {
      this.closeMenu();
    });
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.details.hasAttribute('open')) {
      this.closeMenu();
    } else {
      this.openMenu();
      const megaMenuElement = this.querySelector('mega-menu');
      if (megaMenuElement) {
        setTimeout(() => {
          megaMenuElement.calculateMaxHeight();
        }, 50);
      }
    }
  }

  openMenu() {
    // First, remove is-open class from all other menus
    const otherMenus = document.querySelectorAll('main-menu details.is-open');
    otherMenus.forEach(details => {
      if (details !== this.details) {
        details.classList.remove('is-open');
        const mainMenu = details.closest('main-menu');
        if (mainMenu) {
          mainMenu.closeMenu();
        }
      }
    });

    // Immediately add the is-open class
    this.details.classList.add('is-open');

    // Then set other attributes
    this.details.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');

    // Animate opening
    if (this.megaMenu) {
      const megaMenuElements = this.megaMenu.querySelectorAll('.mega-menu__body, .mega-menu__aside');
      this.megaMenu.style.opacity = '0';
      this.megaMenuInner.style.transform = 'translateY(-100%)';
      megaMenuElements.forEach(element => {
        element.style.transform = 'translateY(-100%)';
      });

      requestAnimationFrame(() => {
        this.megaMenu.style.transition = 'opacity 0.1s ease-out';
        this.megaMenuInner.style.transition = 'transform 0.5s ease-out';
        megaMenuElements.forEach(element => {
          element.style.transition = 'transform 0.6s ease-out';
        });
        this.megaMenu.style.opacity = '1';
        this.megaMenuInner.style.transform = 'translateY(0)';
        megaMenuElements.forEach(element => {
          element.style.transform = 'translateY(0)';
        });
      });
    }
  }

  closeMenu() {
    // Immediately remove the is-open class
    this.details.classList.remove('is-open');

    // Reset background height
    // Unlock scroll is now handled by the observer

    if (this.megaMenu) {
      // Animate closing
      const megaMenuElements = this.megaMenu.querySelectorAll('.mega-menu__body, .mega-menu__aside');
      this.megaMenu.style.opacity = '0';
      this.megaMenuInner.style.transform = 'translateY(-100%)';
      megaMenuElements.forEach(element => {
        element.style.transform = 'translateY(-100%)';
      });

      // Create a function to clean up the menu state
      const cleanupMenu = () => {
        this.details.removeAttribute('open');
        this.summary.setAttribute('aria-expanded', 'false');

        // Reset styles
        this.megaMenu.style.transition = '';
        this.megaMenuInner.style.transition = '';
        this.megaMenu.style.opacity = '';
        this.megaMenuInner.style.transform = '';
        megaMenuElements.forEach(element => {
          element.style.transition = '';
          element.style.transform = '';
        });
      };

      // Remove other attributes after animation
      this.megaMenu.addEventListener('transitionend', cleanupMenu, { once: true });

      // Fallback timeout to ensure cleanup happens even if transition doesn't complete
      // This handles cases where user hovers out before transition completes
      const fallbackTimeout = setTimeout(() => {
        // Remove the transitionend listener since we're handling cleanup manually
        this.megaMenu.removeEventListener('transitionend', cleanupMenu);
        cleanupMenu();
      }, 600); // Slightly longer than the longest transition (0.6s)

      // Clear the fallback timeout if transition completes normally
      this.megaMenu.addEventListener('transitionend', () => {
        clearTimeout(fallbackTimeout);
      }, { once: true });
    }
  }
}

customElements.define('main-menu', MainMenu);
